import React, { useCallback, useMemo, useRef } from 'react';
import GridLayout, { useContainerWidth, verticalCompactor, noCompactor } from 'react-grid-layout';
import WidgetFrame from './WidgetFrame';
import { widgetMeta } from './registry';

export const ROW_H = 36;
export const MARGIN = 12;

const same = (a, b) => a.length === b.length && a.every(x => { const y = b.find(z => z.i === x.i); return y && y.x === x.x && y.y === x.y && y.w === x.w && y.h === x.h; });

export default function BoardGrid({ board, boards, locked, user, onLayout, onConfig, onRemove, onMove, onOpenConfig, onDragMove, onDragEnd }) {
  const { width, containerRef, mounted } = useContainerWidth();
  const cols = board.cols || 12;
  const layout = useMemo(() => (board.layout || []).map(it => {
    const m = widgetMeta(board.widgets?.[it.i]?.type);
    return { ...it, minW: m.minW || 1, minH: m.minH || 1, static: locked };
  }), [board.layout, board.widgets, locked]);
  const last = useRef(board.layout);

  const handleLayout = useCallback((l) => {
    const clean = l.map(({ i, x, y, w, h }) => ({ i, x, y, w, h }));
    if (same(clean, board.layout || [])) return;
    last.current = clean;
    onLayout(clean);
  }, [board.layout, onLayout]);

  const narrow = mounted && width < 700;
  const frame = (it) => {
    const w = board.widgets?.[it.i];
    if (!w) return null;
    return <WidgetFrame wid={it.i} widget={w} boardId={board.id} boards={boards} locked={locked || narrow} size={{ w: narrow ? 4 : it.w, h: it.h }} user={user}
      onConfig={(c) => onConfig(it.i, c)} onRemove={() => onRemove(it.i)} onMove={(to) => onMove(it.i, to)} onOpenConfig={() => onOpenConfig(it.i)} />;
  };
  if (narrow) {
    // phones: one column, tiles stacked in layout order — editing happens on a desktop
    const ordered = [...(board.layout || [])].sort((a, b) => a.y - b.y || a.x - b.x);
    return (
      <div ref={containerRef} className="w-full space-y-3" data-testid="board-grid">
        {ordered.map(it => <div key={it.i} style={{ height: it.h * ROW_H + (it.h - 1) * MARGIN }} data-testid={`grid-item-${it.i}`}>{frame(it)}</div>)}
      </div>
    );
  }
  return (
    <div ref={containerRef} className="w-full min-h-[60vh]" data-testid="board-grid">
      {mounted && (
        <GridLayout
          width={width}
          layout={layout}
          gridConfig={{ cols, rowHeight: ROW_H, margin: [MARGIN, MARGIN], containerPadding: [0, 0] }}
          dragConfig={{ enabled: !locked, handle: '.widget-drag', cancel: '.no-drag', bounded: false, threshold: 4 }}
          resizeConfig={{ enabled: !locked, handles: ['se', 'e', 's'] }}
          compactor={board.compact ? verticalCompactor : noCompactor}
          onLayoutChange={handleLayout}
          onDrag={(l, oldItem, newItem, ph, e) => onDragMove?.(e, newItem?.i)}
          onDragStop={(l, oldItem, newItem, ph, e) => onDragEnd?.(e, newItem?.i)}
          className="board-layout"
        >
          {(board.layout || []).filter(it => board.widgets?.[it.i]).map(it => <div key={it.i} data-testid={`grid-item-${it.i}`}>{frame(it)}</div>)}
        </GridLayout>
      )}
    </div>
  );
}
